/**
 * Chat Input Component
 * 
 * Input area for typing and sending chat messages with framework suggestions.
 * Memoized for performance optimization.
 */

import { memo } from "react";

import { Send, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { SessionFrameworkWidget } from "./SessionFrameworkWidget";

import type { Message } from "@/types/state";

interface ChatInputProps {
  sessionId: string;
  input: string;
  sending: boolean;
  showFrameworkWidget: boolean;
  recentMessages: Message[];
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onToggleFrameworkWidget: () => void;
  onSelectFramework: (frameworkKey: string) => void;
  disabled?: boolean;
}

const ChatInputComponent = ({
  sessionId,
  input,
  sending,
  showFrameworkWidget,
  recentMessages,
  onInputChange,
  onSendMessage,
  onToggleFrameworkWidget,
  onSelectFramework,
  disabled,
}: ChatInputProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSendMessage();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleFrameworkWidget}
          disabled={disabled}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {showFrameworkWidget ? "Hide" : "Show"} Frameworks
        </Button>
      </div>

      {showFrameworkWidget && (
        <SessionFrameworkWidget
          sessionId={sessionId}
          recentMessages={recentMessages}
          onSelectFramework={onSelectFramework}
          disabled={sending || disabled}
        />
      )}

      <div className="flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message... (Shift+Enter for new line)"
          className="min-h-[80px]"
          disabled={sending || disabled}
        />
        <Button onClick={onSendMessage} disabled={sending || disabled || !input.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const ChatInput = memo(ChatInputComponent);
